import { useState, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams, Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { logEdgeFunctionError } from "@/utils/sentry";
import LoginForm from "@/components/auth/LoginForm";
import RegisterForm from "@/components/auth/RegisterForm";
import ResetPasswordForm from "@/components/auth/ResetPasswordForm";
import { AuthSwitch } from "@/components/ui/auth-switch";

const Auth = () => {
  const hasPreselectedPlan = !!localStorage.getItem("selected_plan");
  const [searchParams] = useSearchParams();
  const modeParam = searchParams.get("mode");
  const isTrialIntent = searchParams.get("trial") === "true";
  const defaultMode: "login" | "register" =
    modeParam === "register" || modeParam === "login"
      ? modeParam
      : hasPreselectedPlan || isTrialIntent
      ? "register"
      : "login";
  const [isResetMode, setIsResetMode] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuth();

  useEffect(() => {
    if (!loading && user) {
      const checkOnboardingAndSubscription = async () => {
        try {
          const { data: profile } = await supabase
            .from("user_profiles")
            .select("onboarding_started, instagram_username, category, subscription_cancelled_at")
            .eq("user_id", user.id)
            .maybeSingle();

          // Step 1 — no profile row at all (or onboarding flag never set):
          // user has not completed Instagram setup. Restart from step 1.
          if (!profile || profile.onboarding_started === false) {
            navigate("/onboarding/instagram", { replace: true });
            return;
          }

          // Step 2 — has a profile but no category yet. Drop them at the
          // industry picker rather than at /subscribe, since the picker is
          // mandatory before payment makes sense.
          if (!profile.category) {
            navigate("/onboarding/industry", { replace: true });
            return;
          }

          const { data: subData } = await supabase.functions.invoke("check-subscription");
          const hasSubscription = subData?.subscribed === true;

          if (!hasSubscription) {
            // Returning subscriber whose plan was cancelled: send them
            // straight to /subscribe with the cancelled banner so they can
            // resume payment without walking back through onboarding.
            if (profile.subscription_cancelled_at) {
              navigate("/subscribe?reason=cancelled", { replace: true });
              return;
            }
            // Net-new user who completed mandatory onboarding (Instagram +
            // category) but never paid: drop them at the last form step
            // (competitors). They can review/edit inspirations & competitors
            // and proceed forward to pricing — instead of being dumped on
            // the pricing screen with no context about what's saved.
            navigate("/onboarding/add-competitors", { replace: true });
            return;
          }

          const from = location.state?.from?.pathname || "/panel";
          navigate(from, { replace: true });
        } catch (error) {
          logEdgeFunctionError('check-subscription', error, { context: 'auth_redirect' });
          navigate("/panel", { replace: true });
        }
      };

      checkOnboardingAndSubscription();
    }
  }, [user, loading, navigate, location]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FEF3EC] via-[#F9B896] to-[#F5A07A] flex items-center justify-center px-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FEF3EC] via-[#F9B896] to-[#F5A07A] flex flex-col items-center justify-center px-4 py-8">
      {/* Logo */}
      <div className="text-center mb-8">
        <Link to="/" className="hover:opacity-80 transition-opacity">
          <h1 className="text-3xl font-bold text-primary tracking-tight">Viraluj.pl</h1>
        </Link>
        <p className="text-muted-foreground mt-1 text-sm">
          {isResetMode
            ? "Odzyskaj dostęp do konta"
            : "Zaloguj się lub utwórz konto, aby rozpocząć"}
        </p>
      </div>

      {/* Trial intent banner */}
      {isTrialIntent && !isResetMode && (
        <div className="w-full max-w-[440px] mb-4 rounded-2xl bg-white/90 border border-primary/30 px-5 py-3 text-center shadow-sm">
          <p className="text-sm font-semibold text-primary">
            5 dni za darmo
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Bez zobowiązań · Anuluj w każdej chwili
          </p>
        </div>
      )}

      <AnimatePresence mode="wait">
        {isResetMode ? (
          <motion.div
            key="reset"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            className="w-full max-w-[440px]"
          >
            <div className="bg-card rounded-2xl shadow-[var(--shadow-card)] border border-border/50 p-6 sm:p-8">
              <ResetPasswordForm onBack={() => setIsResetMode(false)} />
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="auth-switch"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            className="w-full max-w-[1150px]"
          >
            <AuthSwitch
              defaultMode={defaultMode}
              loginForm={<LoginForm onResetPassword={() => setIsResetMode(true)} />}
              registerForm={<RegisterForm />}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Auth;
